
import React from 'react';
import { Job } from "@/components/jobs/types";
import { useAuth } from '@/contexts/AuthContext';

import { EditableJobHeader } from './EditableJobHeader';
import { JobExecutiveSummaryCard } from './JobExecutiveSummaryCard';
import { JobCriticalFactsSidebar } from './JobCriticalFactsSidebar';
import { QuickCandidateAssessment } from './QuickCandidateAssessment';
import { EditableJobDescription } from './EditableJobDescription';
import { EditableJobRequirements } from './EditableJobRequirements';
import { EditableJobSkills } from './EditableJobSkills';
import { JobOverviewDetails } from './JobOverviewDetails';
import { JobOverviewDocuments } from './JobOverviewDocuments';
import { JobOverviewActions } from './JobOverviewActions';
import { useJobOverviewForm } from './hooks/useJobOverviewForm';

interface JobOverviewProps {
  job: Job;
}

export const JobOverview = ({ job }: JobOverviewProps) => {
  const { user } = useAuth();
  const {
    isEditing,
    setIsEditing,
    formData,
    handleInputChange,
    handleSelectChange,
    handleSave,
    handleCancel,
    isUpdating,
    formatDate,
  } = useJobOverviewForm({ job });

  const canEdit = user && job.user_id === user.id;

  if (isEditing) {
    // Show the detailed edit form when in editing mode
    return (
      <div className="space-y-6">
        <div className="grid gap-4">
          <JobOverviewDetails
            job={job}
            isEditing={isEditing}
            formData={{ 
              location: formData.location,
              employment_type: formData.employment_type,
              salary_min: formData.salary_min,
              salary_max: formData.salary_max,
              status: formData.status,
              skills: formData.skills,
              experience_required: formData.experience_required,
              source_url: formData.source_url
            }}
            onInputChange={handleInputChange}
            onSelectChange={handleSelectChange}
            formatDate={formatDate}
          />
          <JobOverviewDocuments job={job} isEditing={isEditing} />
        </div>
        <JobOverviewActions
          canEdit={canEdit}
          isEditing={isEditing}
          isUpdating={isUpdating}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      </div>
    );
  }

  // Enhanced overview layout with inline editing capabilities
  return (
    <div className="space-y-6">
      {/* Editable Header */}
      <EditableJobHeader job={job} canEdit={canEdit} />

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Executive Summary */}
          <JobExecutiveSummaryCard job={job} />
          
          {/* Quick Candidate Assessment */}
          <QuickCandidateAssessment job={job} />
          
          {/* Editable Job Description */}
          <EditableJobDescription job={job} canEdit={canEdit} />
          
          {/* Editable Requirements */}
          <EditableJobRequirements job={job} canEdit={canEdit} />
          
          {/* Editable Skills */}
          <EditableJobSkills job={job} canEdit={canEdit} />
        </div>

        {/* Right Column - Critical Facts Sidebar */}
        <div className="lg:col-span-1">
          <JobCriticalFactsSidebar job={job} />
        </div>
      </div>
    </div>
  );
};
